// In-tour LLM chat assistant — Phase 6 wow.
//
// Floating chat bubble in the bottom-right corner. Topbar 💬 button opens
// it; chat history persists in localStorage so the buyer's question history
// survives reload + room navigation. POSTs to /api/chat with the current
// rooms.json context so Claude can answer "qual o pé direito da sala?"
// using ground truth rather than hallucination.
//
// Architectural shape mirrors the other UI panels (savedSessions, qrPanel):
// • install() wires DOM + topbar button
// • open / close toggle a CSS class on the root
// • localStorage shadow keyed `imersivo:chat-history` (room-agnostic so the
//   conversation can span navigation)
//
// Privacy note: the chat content (buyer's questions + assistant replies)
// stays in localStorage on the buyer's device. Nothing is uploaded to Blob
// or persisted server-side; each /api/chat call is stateless. If you later
// want the rep to read the buyer's chat history, route it through
// BuyerSession.chatHistory (same pattern as BuyerSession.notes).

import { showToast } from './toast';
import { log } from '../app/logger';
import type { ChatContext } from '../app/App';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  ts: number;
}

const STORAGE_DRAFT_KEY = 'imersivo:chat-history';
const MAX_HISTORY_TURNS = 20; // server caps at 30; keep some headroom

export interface ChatPanelDeps {
  /** Returns the rooms-context block to send with each chat request. The
   *  shape is owned by App.buildChatContext so the chat assistant gets
   *  everything in rooms.json that's worth grounding an answer on. */
  getContext: () => ChatContext;
}

export class ChatPanel {
  private root: HTMLElement | null = null;
  private listEl: HTMLElement | null = null;
  private inputEl: HTMLTextAreaElement | null = null;
  private sendBtn: HTMLButtonElement | null = null;
  private toggleBtn: HTMLButtonElement | null = null;
  private statusEl: HTMLElement | null = null;
  private history: ChatMessage[] = [];
  private busy = false;
  private readonly deps: ChatPanelDeps;
  /** Optional storage-key suffix — set to the buyer's session code when
   *  a `?s=<code>` URL is hydrated so chat history is scoped to that
   *  buyer's URL, not the device. Mirrors NotesManager.setStorageScope.
   *  Without this, a rep handing the tablet from buyer A to buyer B
   *  would expose A's full chat to B on the next open. */
  private storageScope: string | null = null;

  constructor(deps: ChatPanelDeps) {
    this.deps = deps;
  }

  install(): void {
    this.toggleBtn = document.getElementById('chat-button') as HTMLButtonElement | null;
    this.root = document.getElementById('chat-panel');
    this.listEl = document.getElementById('chat-panel-messages');
    this.inputEl = document.getElementById('chat-panel-input') as HTMLTextAreaElement | null;
    this.sendBtn = document.getElementById('chat-panel-send') as HTMLButtonElement | null;
    this.statusEl = document.getElementById('chat-panel-status');
    const closeBtn = document.getElementById('chat-panel-close') as HTMLButtonElement | null;

    if (!this.root || !this.listEl || !this.inputEl || !this.sendBtn || !this.toggleBtn) {
      console.warn('[chat] DOM elements missing — install skipped');
      return;
    }

    this.toggleBtn.addEventListener('click', () => this.toggle());
    closeBtn?.addEventListener('click', () => this.close());
    this.sendBtn.addEventListener('click', () => void this.send());
    this.inputEl.addEventListener('keydown', (e) => {
      // Enter sends, Shift+Enter inserts newline (standard chat UX).
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void this.send();
      }
    });

    this.loadHistory();
    this.renderMessages();
    // If history is empty, seed a welcome message so the panel doesn't open
    // as a blank box — onboards the buyer to what they can ask.
    if (this.history.length === 0) {
      this.appendAssistantMessage(
        'Olá! Sou o assistente do tour. Posso responder sobre os ambientes, materiais disponíveis, ou ajudar com a navegação. O que gostaria de saber?',
      );
    }
  }

  toggle(): void {
    if (!this.root) return;
    if (this.root.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (!this.root) return;
    this.root.classList.add('is-open');
    this.toggleBtn?.setAttribute('aria-pressed', 'true');
    // Focus the input on open so the buyer can start typing immediately.
    queueMicrotask(() => this.inputEl?.focus());
  }

  close(): void {
    if (!this.root) return;
    this.root.classList.remove('is-open');
    this.toggleBtn?.setAttribute('aria-pressed', 'false');
  }

  private async send(): Promise<void> {
    if (this.busy) return;
    const text = this.inputEl?.value.trim() ?? '';
    if (text.length === 0) return;
    if (text.length > 800) {
      showToast('Mensagem muito longa (máximo 800 caracteres)', { tone: 'error' });
      return;
    }
    this.busy = true;
    this.setStatus('Pensando…');
    this.appendUserMessage(text);
    if (this.inputEl) this.inputEl.value = '';

    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          messages: this.history
            .slice(-MAX_HISTORY_TURNS)
            .map(({ role, content }) => ({ role, content })),
          context: this.deps.getContext(),
        }),
      });
      if (res.status === 501) {
        this.setStatus('');
        this.appendAssistantMessage(
          '⚠️ O chat ainda não está configurado neste deploy. Peça ao operador para ativar o ANTHROPIC_API_KEY no Vercel.',
        );
        return;
      }
      if (res.status === 429) {
        this.setStatus('');
        this.appendAssistantMessage('Muitas perguntas seguidas — espere alguns segundos e tente novamente.');
        return;
      }
      if (!res.ok) {
        this.setStatus('');
        log.event('chat_failed', { status: res.status });
        this.appendAssistantMessage(`Erro ${res.status} — tente novamente em instantes.`);
        return;
      }
      const body = (await res.json()) as { reply?: string; error?: string };
      this.setStatus('');
      if (body.reply) {
        this.appendAssistantMessage(body.reply);
      } else {
        this.appendAssistantMessage(body.error ?? 'Resposta vazia. Tente reformular a pergunta.');
      }
    } catch (err) {
      this.setStatus('');
      log.event('chat_failed', { reason: 'network', detail: String(err) });
      this.appendAssistantMessage('Falha de rede. Tente novamente.');
    } finally {
      this.busy = false;
      this.inputEl?.focus();
    }
  }

  private appendUserMessage(content: string): void {
    this.history.push({ role: 'user', content, ts: Date.now() });
    this.persistHistory();
    this.renderMessages();
  }

  private appendAssistantMessage(content: string): void {
    this.history.push({ role: 'assistant', content, ts: Date.now() });
    this.persistHistory();
    this.renderMessages();
  }

  private renderMessages(): void {
    if (!this.listEl) return;
    this.listEl.innerHTML = '';
    for (const msg of this.history.slice(-MAX_HISTORY_TURNS)) {
      const li = document.createElement('div');
      li.className = `chat-msg chat-msg--${msg.role}`;
      const bubble = document.createElement('div');
      bubble.className = 'chat-msg__bubble';
      bubble.textContent = msg.content;
      li.appendChild(bubble);
      this.listEl.appendChild(li);
    }
    // Auto-scroll to the latest message.
    queueMicrotask(() => {
      if (this.listEl) this.listEl.scrollTop = this.listEl.scrollHeight;
    });
  }

  private setStatus(text: string): void {
    if (this.statusEl) this.statusEl.textContent = text;
  }

  /** Re-key the chat history storage to a session-scoped slot. Called by
   *  App after a `?s=<code>` URL is hydrated. Loads the new scope's
   *  history (which may be empty for a buyer first seeing the URL) and
   *  re-renders. Mirrors NotesManager.setStorageScope. */
  setStorageScope(sessionCode: string | null): void {
    if (this.storageScope === sessionCode) return;
    this.storageScope = sessionCode;
    this.history = [];
    this.loadHistory();
    this.renderMessages();
  }

  private storageKey(): string {
    return this.storageScope ? `${STORAGE_DRAFT_KEY}:${this.storageScope}` : STORAGE_DRAFT_KEY;
  }

  private loadHistory(): void {
    try {
      const raw = localStorage.getItem(this.storageKey());
      if (!raw) return;
      const parsed = JSON.parse(raw) as ChatMessage[];
      if (Array.isArray(parsed)) this.history = parsed;
    } catch {
      /* corrupted / private mode — ignore */
    }
  }

  private persistHistory(): void {
    try {
      // Trim aggressively so localStorage doesn't grow unbounded across
      // many sessions. We display the last MAX_HISTORY_TURNS anyway.
      const trimmed = this.history.slice(-MAX_HISTORY_TURNS * 2);
      localStorage.setItem(this.storageKey(), JSON.stringify(trimmed));
    } catch {
      /* quota / private mode — runtime memory still works */
    }
  }
}
